import { Position } from '../core/GridGeometry.js';
import { Grid } from '../core/Grid.js';

/**
 * Shared color palette for all renderers.
 */
export const COLORS = {
    // Primary
    teal: '#4ecdc4',
    red: '#e94560',
    amber: '#fbbf24',

    // Secondary
    blue: '#40a4df',
    purple: '#a78bfa',

    // Neutral
    white: '#ffffff',
    gray: '#888888',
    darkGray: '#666666',
    borderGray: '#5a5a8c',
    lineGray: '#3a3a5c',
    background: '#1a1a2e'
};

export const FONT_FAMILY = 'Lexend, sans-serif';

export function canvasFont(size: number, bold: boolean = true): string {
    return `${bold ? 'bold ' : ''}${size}px ${FONT_FAMILY}`;
}

/**
 * Return an rgba() string for a named color with the given alpha.
 */
export function colorWithAlpha(name: keyof typeof COLORS, alpha: number): string {
    const hex = COLORS[name];
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export interface RenderConfig {
    cellSize: number;
    padding: number;
    gridLineColor: string;
    gridLineWidth: number;
    backgroundColor: string;
    digitColor: string;
    digitFont: string;
    gridSize: number;
}

export const DEFAULT_CONFIG: RenderConfig = {
    cellSize: 50,
    padding: 20,
    gridLineColor: COLORS.lineGray,
    gridLineWidth: 1,
    backgroundColor: COLORS.background,
    digitColor: COLORS.white,
    digitFont: canvasFont(24),
    gridSize: 10
};

export class Renderer {
    protected canvas: HTMLCanvasElement;
    protected ctx: CanvasRenderingContext2D;
    protected config: RenderConfig;
    protected grid: Grid;
    protected logicalSize: number = 0;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: Partial<RenderConfig> = {}) {
        this.canvas = canvas;
        this.grid = grid;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get 2D context');
        this.ctx = ctx;

        this.config = { ...DEFAULT_CONFIG, gridSize: grid.size, ...config };
        this.setupCanvas();
    }

    protected setupCanvas(): void {
        const size = this.config.cellSize * this.grid.size + this.config.padding * 2;
        const scaleFactor = Math.max(window.devicePixelRatio || 1, 2);
        this.canvas.width = size * scaleFactor;
        this.canvas.height = size * scaleFactor;
        this.canvas.dataset.scaleFactor = String(scaleFactor);
        this.ctx.scale(scaleFactor, scaleFactor);
        this.logicalSize = size;
    }

    protected getCellCenter(pos: Position): { x: number; y: number } {
        return {
            x: this.config.padding + pos.col * this.config.cellSize + this.config.cellSize / 2,
            y: this.config.padding + pos.row * this.config.cellSize + this.config.cellSize / 2
        };
    }

    protected getCellTopLeft(pos: Position): { x: number; y: number } {
        return {
            x: this.config.padding + pos.col * this.config.cellSize,
            y: this.config.padding + pos.row * this.config.cellSize
        };
    }

    positionFromPixel(x: number, y: number): Position | null {
        const col = Math.floor((x - this.config.padding) / this.config.cellSize);
        const row = Math.floor((y - this.config.padding) / this.config.cellSize);

        if (row >= 0 && row < this.grid.size && col >= 0 && col < this.grid.size) {
            return { row, col };
        }
        return null;
    }

    clear(): void {
        this.ctx.fillStyle = this.config.backgroundColor;
        this.ctx.fillRect(0, 0, this.logicalSize, this.logicalSize);
    }

    getConfig(): RenderConfig {
        return this.config;
    }
}
